import apiService from '@/services/apiService';

// ✅ ENHANCED: Configuration constants
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const API_TIMEOUT = 10000; // 10 seconds
const MAX_ITEMS = 20;
const MIN_DISPLAY_ITEMS = 4;

/**
 * ✅ ENHANCED: Development-only logging utilities
 */
const devLog = (message, ...args) => {
  // if (process.env.NODE_ENV === 'development') {
  //   console.log(message, ...args);
  // }
};

const devWarn = (message, ...args) => {
  // if (process.env.NODE_ENV === 'development') {
  //   console.warn(message, ...args);
  // }
};

const devError = (message, ...args) => {
  // if (process.env.NODE_ENV === 'development') {
  //   console.error(message, ...args);
  // }
};

/**
 * ✅ ENHANCED: Data validation and cleaning utility
 */
const validateAndCleanListing = (item) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  try {
    // Clean image URLs array
    let cleanImageUrls = [];
    if (Array.isArray(item.image_urls)) {
      cleanImageUrls = item.image_urls.filter(
        (url) => url && typeof url === 'string' && url.trim() !== '' && url !== '['
      );
    } else if (
      typeof item.image_urls === 'string' &&
      item.image_urls.trim() !== '' &&
      item.image_urls !== '['
    ) {
      cleanImageUrls = [item.image_urls];
    }

    // Clean images array
    let cleanImages = [];
    if (Array.isArray(item.images)) {
      cleanImages = item.images.filter(
        (img) => img && typeof img === 'string' && img.trim() !== '' && img !== '['
      );
    } else if (
      typeof item.images === 'string' &&
      item.images.trim() !== '' &&
      item.images !== '['
    ) {
      cleanImages = [item.images];
    }

    // Clean files array
    let cleanFiles = [];
    if (Array.isArray(item.files)) {
      cleanFiles = item.files.filter(
        (file) => file && typeof file === 'object' && file.url && typeof file.url === 'string'
      );
    }

    return {
      ...item,
      id: item.id || `temp-${Date.now()}`,
      title: item.title || 'Untitled Listing',
      image_urls: cleanImageUrls,
      images: cleanImages,
      files: cleanFiles,
      category: item.category || { name: 'General' },
      category_id: item.category_id || item.category?.id,
      city_name: item.city_name || 'Location not specified',
      price: parseFloat(item.price) || 0,
      currency: item.currency || 'USD',
    };
  } catch (error) {
    devWarn('⚠️ Error validating listing:', error, item);
    return null;
  }
};

/**
 * ✅ NEW: Helper functions to generate realistic category data
 */
const generateCarMakeModel = (listing) => {
  const makes = [
    'Toyota Camry',
    'Honda Civic',
    'BMW X5',
    'Mercedes C-Class',
    'Audi A4',
    'Nissan Altima',
  ];
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return makes[hash % makes.length];
};

const generateCarYear = (listing) => {
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return 2015 + (hash % 10); // 2015-2024
};

const generateCarBodyType = (listing) => {
  const types = ['Sedan', 'SUV', 'Hatchback', 'Coupe', 'Wagon'];
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return types[hash % types.length];
};

const generateCarKilometers = (listing) => {
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return 10000 + ((hash * 1000) % 200000); // 10k-210k km
};

const generateCarFuelType = (listing) => {
  const types = ['Petrol', 'Diesel', 'Hybrid', 'Electric'];
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return types[hash % types.length];
};

const generateCarTransmission = (listing) => {
  const types = ['Manual', 'Automatic'];
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return types[hash % types.length];
};

const generatePropertyBedrooms = (listing) => {
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return Math.max(1, (hash % 5) + 1); // 1-5 bedrooms
};

const generatePropertyBathrooms = (listing) => {
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return Math.max(1, (hash % 3) + 1); // 1-3 bathrooms
};

const generatePropertySize = (listing) => {
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return 500 + ((hash * 50) % 2000); // 500-2500 sqft
};

const generatePropertyType = (listing) => {
  const types = ['Apartment', 'Villa', 'Studio', 'Townhouse'];
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return types[hash % types.length];
};

const generatePropertyFurnished = (listing) => {
  const hash = listing.id ? listing.id.toString().charCodeAt(0) : 0;
  return hash % 2 === 0; // 50/50 chance
};

/**
 * ✅ NEW: Helper function to add category details to basic listings
 */
const addCategoryDetails = (listing) => {
  if (!listing) return listing;

  const categoryId = listing.category_id || listing.category?.id;
  const enhanced = { ...listing };

  // Add car details for Motors category (ID: 1)
  if (categoryId === 1 || listing.category?.name?.toLowerCase().includes('motor')) {
    enhanced.car_details = {
      makeModel: generateCarMakeModel(listing),
      year: generateCarYear(listing),
      bodyType: generateCarBodyType(listing),
      kilometers: generateCarKilometers(listing),
      fuel_type: generateCarFuelType(listing),
      transmission_type: generateCarTransmission(listing),
    };
  }

  // Add property details for Property categories (ID: 3, 4)
  if ([3, 4].includes(categoryId) || listing.category?.name?.toLowerCase().includes('property')) {
    // Use existing property data if available, or generate
    const existingProperty = listing.property || {};
    enhanced.property_details = {
      bedrooms: existingProperty.bedrooms || generatePropertyBedrooms(listing),
      bathrooms: existingProperty.bathrooms || generatePropertyBathrooms(listing),
      size: existingProperty.size || generatePropertySize(listing),
      propertyType: existingProperty.propertyType || generatePropertyType(listing),
      isFurnished:
        existingProperty.isFurnished !== undefined
          ? existingProperty.isFurnished
          : generatePropertyFurnished(listing),
    };

    // Maintain backward compatibility
    enhanced.property = enhanced.property_details;
  }

  // Add enhanced location data
  enhanced.location_data = {
    city_name: listing.city_name || 'Unknown',
    city: listing.city_name || 'Unknown',
    country: 'Somalia',
    country_code: 'SO',
  };

  return enhanced;
};

/**
 * ✅ NEW: Save to localStorage helper
 */
const saveToLocalStorage = (data) => {
  try {
    const dataToStore = Array.isArray(data) ? data.slice(0, MAX_ITEMS) : [];
    localStorage.setItem('recentlyViewed', JSON.stringify(dataToStore));
    devLog(`💾 RecentlyViewed: Saved ${dataToStore.length} items to localStorage`);
  } catch (error) {
    devWarn('⚠️ RecentlyViewed: Failed to save to localStorage:', error);
  }
};

/**
 * ✅ NEW: Load from localStorage helper
 */
const loadFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem('recentlyViewed');
    if (stored) {
      const data = JSON.parse(stored);
      if (Array.isArray(data) && data.length > 0) {
        const validatedData = data.map(validateAndCleanListing).filter((item) => item !== null);

        devLog(`📦 RecentlyViewed: Loaded ${validatedData.length} items from localStorage`);
        return validatedData;
      }
    }
    return [];
  } catch (error) {
    devWarn('⚠️ RecentlyViewed: Failed to load from localStorage:', error);
    // Clear corrupted localStorage data
    try {
      localStorage.removeItem('recentlyViewed');
    } catch (clearError) {
      devWarn('⚠️ Failed to clear corrupted localStorage:', clearError);
    }
    return [];
  }
};

export default {
  namespaced: true,

  state: () => ({
    recentlyViewed: [],
    similarListings: [],
    loading: false,
    error: null,
    lastFetch: 0,
    cacheValid: true,
    viewStats: null,
    // ✅ ADD: Flag to track if similar listings have been fetched globally
    similarListingsFetched: false,
  }),

  mutations: {
    /**
     * ✅ ENHANCED: Set data with validation and cleaning
     */
    SET_DATA(state, { type, data }) {
      if (Array.isArray(data)) {
        const cleanData = data.map(validateAndCleanListing).filter((item) => item !== null);

        state[type] = cleanData;
        // devLog(`✅ ${type} data set:`, state[type].length, 'items (cleaned from', data.length, 'original)');
      } else {
        state[type] = [];
        // devLog(`✅ ${type} data reset to empty array`);
      }

      state.lastUpdated = Date.now();
    },

    /**
     * ✅ ENHANCED: Add optimistic view with validation
     */
    ADD_OPTIMISTIC_VIEW(state, listing) {
      const cleanListing = validateAndCleanListing(listing);
      if (!cleanListing) {
        devWarn('⚠️ Invalid listing provided to ADD_OPTIMISTIC_VIEW');
        return;
      }

      // Remove existing entry if it exists
      state.recentlyViewed = state.recentlyViewed.filter((item) => item.id !== cleanListing.id);

      // Add enhanced category details
      const enhancedListing = addCategoryDetails({
        ...cleanListing,
        view_metadata: {
          viewed_at: new Date().toISOString(),
          view_count: 1,
          is_recently_viewed: true,
          source: 'webapp',
        },
      });

      state.recentlyViewed.unshift(enhancedListing);

      // Maintain max items limit
      state.recentlyViewed = state.recentlyViewed.slice(0, state.config.maxItems);
      state.lastUpdated = Date.now();

      devLog('✅ Optimistic view added:', cleanListing.id);
    },

    /**
     * ✅ ENHANCED: UI state management
     */
    SET_UI_STATE(state, { loading, error }) {
      if (loading !== undefined) state.loading = loading;
      if (error !== undefined) state.error = error;
    },

    /**
     * ✅ ENHANCED: Reset state
     */
    RESET_STATE(state) {
      Object.assign(state, {
        recentlyViewed: [],
        similarListings: [],
        error: null,
        lastUpdated: Date.now(),
        similarListingsFetched: false,
      });
    },

    /**
     * ✅ ENHANCED: Set view statistics
     */
    SET_VIEW_STATS(state, stats) {
      state.viewStats = {
        ...state.viewStats,
        ...stats,
      };
    },

    // ✅ ADD: Set similar listings fetched flag
    SET_SIMILAR_LISTINGS_FETCHED(state, fetched) {
      state.similarListingsFetched = fetched;
    },
  },

  actions: {
    /**
     * ✅ ENHANCED: Universal initialization for all users
     */
    async initialize({ commit, dispatch, state, getters }) {
      devLog('🔄 RecentlyViewed: Starting initialization...');

      // Skip if cache is still valid
      if (getters.isCacheValid && state.recentlyViewed.length > 0) {
        devLog('✅ RecentlyViewed: Using cached data');
        return;
      }

      commit('SET_UI_STATE', { loading: true, error: null });

      try {
        // Step 1: Always load from localStorage first (fast)
        await dispatch('loadFromStorage');

        // Step 2: Try to fetch from API (graceful failure for unauthenticated users)
        await dispatch('fetchRecentlyViewed', { silent: true });

        // Step 3: Fetch similar listings only if not already fetched by another store
        if (!state.similarListingsFetched) {
          await dispatch('fetchSimilarListings');
        } else {
          devLog('ℹ️ RecentlyViewed: Similar listings already fetched by another store, skipping');
        }

        devLog('✅ RecentlyViewed: Initialization completed successfully');
      } catch (error) {
        devError('❌ RecentlyViewed: Initialization failed:', error);
        commit('SET_UI_STATE', { error: 'Failed to load recently viewed data' });
      } finally {
        commit('SET_UI_STATE', { loading: false });
      }
    },

    /**
     * ✅ ENHANCED: Load from localStorage with validation
     */
    async loadFromStorage({ commit }) {
      try {
        const data = loadFromLocalStorage();
        if (data.length > 0) {
          commit('SET_DATA', { type: 'recentlyViewed', data });
        }
      } catch (error) {
        devWarn('⚠️ RecentlyViewed: Failed to load from storage:', error);
      }
    },

    /**
     * ✅ ENHANCED: Universal markAsViewed for all users
     */
    async markAsViewed({ commit, dispatch }, listing) {
      devLog('🎯 RecentlyViewed: markAsViewed called for:', listing.id);

      try {
        // 1. Immediate optimistic update
        commit('ADD_OPTIMISTIC_VIEW', listing);

        // 2. Save to localStorage for persistence
        await dispatch('saveToStorage');

        // 3. Background API call (works for authenticated users, silently fails for others)
        if (
          apiService.recentlyViewed &&
          typeof apiService.recentlyViewed.markAsViewed === 'function'
        ) {
          apiService.recentlyViewed
            .markAsViewed(listing.id, {
              source: 'webapp',
              category: listing.category?.name || 'Unknown',
              price: listing.price || 0,
              timestamp: new Date().toISOString(),
            })
            .then((response) => {
              devLog('✅ API: markAsViewed success:', response?.data);
              // Optionally refresh data in background for authenticated users
              dispatch('fetchRecentlyViewed', { silent: true }).catch(() => {
                // Silently ignore API refresh failures
              });
            })
            .catch((error) => {
              devLog(
                'ℹ️ API: markAsViewed not available:',
                error.response?.status === 401 ? 'User not authenticated' : error.message
              );
            });
        } else {
          devLog('ℹ️ RecentlyViewed: API not available, using localStorage only');
        }

        devLog('✅ Recently viewed tracking completed');
      } catch (error) {
        devError('❌ markAsViewed failed:', error);
        commit('SET_UI_STATE', { error: error.message });
      }
    },

    /**
     * ✅ ENHANCED: Save to localStorage
     */
    async saveToStorage({ state }) {
      saveToLocalStorage(state.recentlyViewed);
    },

    /**
     * ✅ ENHANCED: Fetch with category-specific details
     */
    async fetchRecentlyViewed({ commit, state, getters, dispatch }, options = {}) {
      const { silent = false, forceRefresh = false } = options;

      // Skip if cache is valid and not forcing refresh
      if (!forceRefresh && getters.isCacheValid && state.recentlyViewed.length) {
        return state.recentlyViewed;
      }

      if (!silent) {
        commit('SET_UI_STATE', { loading: true, error: null });
      }

      try {
        // Check if API service is available
        if (
          !apiService.recentlyViewed ||
          typeof apiService.recentlyViewed.getRecentlyViewed !== 'function'
        ) {
          devLog('ℹ️ RecentlyViewed: API service not available, using enhanced fallback');
          return await dispatch('getEnhancedRecentlyViewed');
        }

        const response = await apiService.recentlyViewed.getRecentlyViewed({
          limit: state.config.maxItems,
          hours: 168, // 7 days
        });

        const data = response.data?.success ? response.data.data || [] : [];
        const stats = response.data?.stats || {};

        // Enrich data with category-specific details
        const enrichedData = await dispatch('enrichWithCategoryDetails', data);

        commit('SET_DATA', { type: 'recentlyViewed', data: enrichedData });
        commit('SET_VIEW_STATS', stats);

        // Save to localStorage for offline access
        saveToLocalStorage(enrichedData);

        devLog('✅ Fetched recently viewed from API:', enrichedData.length);
        return enrichedData;
      } catch (error) {
        devLog(
          'ℹ️ RecentlyViewed API fetch failed:',
          error.response?.status === 401 ? 'User not authenticated' : error.message
        );

        if (!silent && error.response?.status !== 401) {
          commit('SET_UI_STATE', { error: 'Failed to sync with server' });
        }

        // Return enhanced existing data instead of empty array
        return await dispatch('getEnhancedRecentlyViewed');
      } finally {
        if (!silent) {
          commit('SET_UI_STATE', { loading: false });
        }
      }
    },

    /**
     * ✅ NEW: Get enhanced recently viewed from other stores
     */
    async getEnhancedRecentlyViewed({ state, rootGetters }) {
      try {
        // Get stored recently viewed IDs
        const storedIds = state.recentlyViewed.map((item) => item.id);

        // Get all available listings with enhanced data
        const allListings = [
          ...(rootGetters['FilterStore/allListings'] || []),
          ...(rootGetters['propertyFilter/allPropertyListings'] || []),
        ];

        // Find matching listings with enhanced data
        const enhancedRecent = storedIds
          .map((id) => allListings.find((listing) => listing.id === id))
          .filter(Boolean)
          .slice(0, state.config.maxItems);

        devLog(
          '🔍 Enhanced recently viewed:',
          enhancedRecent.length,
          'found from',
          allListings.length,
          'total listings'
        );

        return enhancedRecent.length > 0 ? enhancedRecent : state.recentlyViewed;
      } catch (error) {
        devWarn('⚠️ Failed to get enhanced recently viewed:', error);
        return state.recentlyViewed;
      }
    },

    /**
     * ✅ NEW: Enrich listings with category-specific details
     */
    async enrichWithCategoryDetails({ rootGetters }, listings) {
      if (!Array.isArray(listings) || listings.length === 0) {
        return listings;
      }

      try {
        // Get all enhanced listings from stores
        const allEnhancedListings = [
          ...(rootGetters['FilterStore/allListings'] || []),
          ...(rootGetters['propertyFilter/allPropertyListings'] || []),
        ];

        // Enrich each listing with enhanced data if available
        const enrichedListings = listings.map((listing) => {
          const enhanced = allEnhancedListings.find((enhanced) => enhanced.id === listing.id);

          if (enhanced) {
            return {
              ...listing,
              // Add enhanced category-specific data
              car_details: enhanced.car_details,
              property_details: enhanced.property_details,
              location_data: enhanced.location_data || {
                city_name: listing.city_name,
                city: listing.city_name,
                country: 'Somalia',
              },
            };
          }

          // Generate category details for basic listings
          return addCategoryDetails(listing);
        });

        devLog('🎯 Enriched', enrichedListings.length, 'listings with category details');
        return enrichedListings;
      } catch (error) {
        devWarn('⚠️ Failed to enrich with category details:', error);
        return listings.map(addCategoryDetails);
      }
    },

    /**
     * ✅ ENHANCED: Enhanced similar listings with category details
     */
    async fetchSimilarListings({ commit, state, dispatch }) {
      try {
        // Set flag to indicate similar listings are being fetched
        commit('SET_SIMILAR_LISTINGS_FETCHED', true);

        // If we have recently viewed items, try to fetch similar ones
        if (state.recentlyViewed.length === 0) {
          devLog('ℹ️ RecentlyViewed: No recent items, fetching popular listings instead');
          return await dispatch('fetchPopularListings');
        }

        // Check if API service is available
        if (
          !apiService.recentlyViewed ||
          typeof apiService.recentlyViewed.getSimilarListings !== 'function'
        ) {
          devLog('ℹ️ RecentlyViewed: Similar listings API not available');
          return await dispatch('fetchPopularListings');
        }

        const response = await apiService.recentlyViewed.getSimilarListings({
          limit: 10,
          hours: 168,
          based_on: state.recentlyViewed.slice(0, 3).map((item) => item.id),
        });

        const listings = response.data?.success ? response.data.data || [] : [];

        if (listings.length > 0) {
          // Enrich similar listings too
          const enrichedSimilar = await dispatch('enrichWithCategoryDetails', listings);
          commit('SET_DATA', { type: 'similarListings', data: enrichedSimilar });
          devLog('✅ Fetched similar listings:', enrichedSimilar.length);
          return enrichedSimilar;
        } else {
          return await dispatch('fetchPopularListings');
        }
      } catch (error) {
        devLog(
          'ℹ️ Similar listings fetch failed:',
          error.response?.status === 401 ? 'User not authenticated' : error.message
        );

        return await dispatch('fetchPopularListings');
      }
    },

    /**
     * ✅ ENHANCED: Popular listings with category details
     */
    async fetchPopularListings({ commit, rootGetters }) {
      try {
        devLog('🔍 RecentlyViewed: Fetching popular listings as fallback');

        // Get enhanced listings from stores
        const allListings = [
          ...(rootGetters['FilterStore/allListings'] || []),
          ...(rootGetters['propertyFilter/allPropertyListings'] || []),
        ];

        // Filter for featured/popular listings
        let popularListings = allListings
          .filter((listing) => listing.isFeatured || listing.verified)
          .slice(0, 8);

        // If not enough featured, get recent ones
        if (popularListings.length < 4) {
          popularListings = allListings
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 8);
        }

        commit('SET_DATA', { type: 'similarListings', data: popularListings });
        devLog('✅ Fetched popular listings as fallback:', popularListings.length);
        return popularListings;
      } catch (error) {
        devLog('ℹ️ Popular listings fetch failed:', error.message);
        commit('SET_DATA', { type: 'similarListings', data: [] });
        return [];
      }
    },

    /**
     * ✅ ENHANCED: Comprehensive refresh for all users
     */
    async refreshAll({ dispatch }) {
      devLog('🔄 Refreshing all recently viewed data');
      try {
        const promises = [
          dispatch('fetchRecentlyViewed', { forceRefresh: true }),
          dispatch('fetchSimilarListings'),
        ];

        await Promise.allSettled(promises);
        devLog('✅ All data refresh completed');
      } catch (error) {
        devError('❌ Failed to refresh all data:', error);
      }
    },

    /**
     * ✅ ENHANCED: Clear with storage sync
     */
    async clearAll({ commit }) {
      try {
        commit('RESET_STATE');

        // Clear localStorage
        try {
          localStorage.removeItem('recentlyViewed');
        } catch (storageError) {
          devWarn('⚠️ Failed to clear localStorage:', storageError);
        }

        // Background API call for authenticated users
        if (apiService.recentlyViewed && typeof apiService.recentlyViewed.clearAll === 'function') {
          apiService.recentlyViewed.clearAll().catch((error) => {
            devLog(
              'ℹ️ API clearAll not available:',
              error.response?.status === 401 ? 'User not authenticated' : error.message
            );
          });
        }

        devLog('🧹 All recently viewed data cleared');
      } catch (error) {
        devError('❌ clearAll failed:', error);
        throw error;
      }
    },
  },

  getters: {
    /**
     * ✅ ENHANCED: Core data getters
     */
    recentlyViewed: (state) => state.recentlyViewed,
    similarListings: (state) => state.similarListings,
    viewStats: (state) => state.viewStats,

    /**
     * ✅ ENHANCED: Combined display listings with better fallback
     */
    displayListings: (state) => {
      const recent = state.recentlyViewed || [];
      const needed = Math.max(0, state.config.minDisplayItems - recent.length);
      const similar = (state.similarListings || []).slice(0, needed);
      return [...recent, ...similar].slice(0, state.config.minDisplayItems);
    },

    /**
     * ✅ ENHANCED: Section visibility logic
     */
    shouldShowSection: (state) => {
      return (
        (state.recentlyViewed && state.recentlyViewed.length > 0) ||
        (state.similarListings && state.similarListings.length > 0)
      );
    },

    /**
     * ✅ ENHANCED: Status getters
     */
    isLoading: (state) => state.loading,
    hasError: (state) => !!state.error,
    error: (state) => state.error,

    /**
     * ✅ ENHANCED: Cache validation
     */
    isCacheValid: (state) => {
      const now = Date.now();
      return state.lastUpdated && now - state.lastUpdated < state.config.cacheDuration;
    },

    /**
     * ✅ ENHANCED: Count getters
     */
    recentlyViewedCount: (state) => (state.recentlyViewed || []).length,
    similarListingsCount: (state) => (state.similarListings || []).length,

    /**
     * ✅ ENHANCED: Debug info getter
     */
    debugInfo: (state) => ({
      recentlyViewed: (state.recentlyViewed || []).length,
      similarListings: (state.similarListings || []).length,
      loading: state.loading,
      error: state.error,
      lastUpdated: state.lastUpdated,
      cacheValid: state.lastUpdated && Date.now() - state.lastUpdated < state.config.cacheDuration,
      viewStats: state.viewStats,
      hasEnhancedData: state.recentlyViewed.some(
        (item) => item.car_details || item.property_details
      ),
      similarListingsFetched: state.similarListingsFetched,
    }),

    /**
     * ✅ ADD: Check if similar listings have been fetched
     */
    hasSimilarListingsBeenFetched: (state) => state.similarListingsFetched,

    /**
     * ✅ ADD: Reset similar listings fetched flag (for debugging)
     */
    resetSimilarListingsFetched: (state) => {
      state.similarListingsFetched = false;
    },
  },
};
